import React, { useState, useRef, useEffect } from 'react';
import { ControlLabel } from 'react-bootstrap';
import { AlertCircle } from 'lucide-react';

// Component imports
import TextMenu from './components/TextMenu';
import ActionButtons from './components/ActionButtons';
import QueryAssistant from './components/QueryAssistant';
import AITextEditorOnboarding from './components/AITextEditorOnboarding';

// Service imports
import aiService from './services/aiService';

// Tracking utilities
import { trackEvent } from '../../utils/posthogTracker';

// Styles
import './AITextEditor.css';

/**
 * AITextEditor Component
 * An enhanced text editor with AI-powered text transformation capabilities
 *
 * Features:
 * - Text area with selection-based AI options menu
 * - AI-powered text transformation options (improve, fix grammar, etc.)
 * - Question answering capability
 * - Accept/Reject UI for AI proposals
 * - Loading indicators during API calls
 */
export default function AITextEditor({
  rows = 3,
  placeholder = '',
  onChange,
  value,
  label,
  context = '',
  lengthPreference = '',
  showOnboarding = false,
  onOnboardingComplete,
  onOnboardingSkip
}) {
  // State for the editor text content
  const [text, setText] = useState(value || '');

  // State for tracking selected text
  const [selection, setSelection] = useState({
    start: null,
    end: null,
    text: ''
  });

  // UI states
  const [menuVisible, setMenuVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingText, setPendingText] = useState(null);
  const [infoResponse, setInfoResponse] = useState(null);
  const [runOnboarding, setRunOnboarding] = useState(showOnboarding);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  // References
  const textareaRef = useRef(null);
  const menuRef = useRef(null);

  /**
   * Handles text selection events to show the AI menu
   */
  const handleSelection = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    if (start !== end) {
      const selectedText = textarea.value.slice(start, end);
      setSelection({ start, end, text: selectedText });
      setMenuVisible(true);
    } else {
      setMenuVisible(false);
    }
  };

  /**
   * Handle clicks outside the menu to close it
   */
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target)
      ) {
        setMenuVisible(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Sync text with parent component value when it changes
   */
  useEffect(() => {
    setText(value || '');
  }, [value]);

  /**
   * Check localStorage for onboarding completion on mount
   */
  useEffect(() => {
    try {
      const completed = localStorage.getItem('ai_text_editor_onboarding_completed') === 'true';
      setHasCompletedOnboarding(completed);
    } catch (error) {
      console.warn('localStorage not available:', error);
      setHasCompletedOnboarding(false);
    }
  }, []);

  /**
   * Update onboarding state when showOnboarding prop changes
   */
  useEffect(() => {
    setRunOnboarding(showOnboarding);
  }, [showOnboarding]);

  /**
   * Handle asking a question to the AI
   * @param {string} query - The question to ask
   */
  const handleAskAI = async query => {
    if (!query || query.trim() === '') return;

    setIsLoading(true);

    try {
      const data = await aiService.askQuestion(query, selection.text, context);
      const result = data.result || '';

      // Store the question and answer
      setInfoResponse({
        question: query,
        answer: result
      });

      setMenuVisible(false);
    } catch (error) {
      console.error('Ask AI error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle AI text transformations
   * @param {string} action - The transformation action (improve, fix_grammar, etc.)
   * @param {string} payloadText - The text to transform
   */
  const handleTransformText = async (action, payloadText) => {
    if (!payloadText || payloadText.trim() === '') return;

    setIsLoading(true);
    setInfoResponse(null); // Clear any previous info response

    try {
      const data = await aiService.processText(
        action,
        payloadText,
        context,
        lengthPreference
      );
      const result = data.result || '';
      setPendingText(result);
      
      // Track text suggestion generation
      if (result) {
        trackEvent('ai_text_suggestion_generated', {
          component: 'AITextEditor',
          suggestion_type: 'text_transformation',
          transformation_action: action,
          original_text_length: payloadText.length,
          suggestion_length: result.length,
          editor_context: context || 'default',
          length_preference: lengthPreference || 'default'
        });
      }
    } catch (error) {
      console.error('Transform text error:', error);
    } finally {
      setIsLoading(false);
      setMenuVisible(false);
    }
  };

  /**
   * Accept the AI-suggested text
   */
  const acceptChange = () => {
    if (pendingText !== null) {
      setText(pendingText);
      onChange?.(pendingText);
      setPendingText(null);
      
      // Track AI suggestion acceptance
      trackEvent('ai_text_suggestion_accepted', {
        component: 'AITextEditor',
        suggestion_type: 'text_transformation',
        editor_context: context || 'default',
        suggestion_length: pendingText.length,
        original_text_length: text.length
      });
    }
  };

  /**
   * Reject the AI-suggested text
   */
  const rejectChange = () => {
    if (pendingText !== null) {
      // Track AI suggestion rejection
      trackEvent('ai_text_suggestion_rejected', {
        component: 'AITextEditor',
        suggestion_type: 'text_transformation',
        editor_context: context || 'default',
        suggestion_length: pendingText.length,
        original_text_length: text.length
      });
    }
    setPendingText(null);
  };

  /**
   * Close the info response panel
   */
  const closeInfoResponse = () => {
    setInfoResponse(null);
  };

  /**
   * Handle demo text insertion during onboarding
   */
  const handleDemoTextInsert = (demoText) => {
    setText(demoText);
    if (onChange) {
      onChange(demoText);
    }
    
    // Track demo text insertion
    trackEvent('ai_text_editor_demo_text_inserted', {
      component: 'AITextEditor',
      demo_text_length: demoText.length,
      timestamp: new Date().toISOString()
    });
  };

  /**
   * Handle onboarding completion
   */
  const handleOnboardingComplete = () => {
    setRunOnboarding(false);
    setHasCompletedOnboarding(true);
    if (onOnboardingComplete) {
      onOnboardingComplete();
    }
  };

  /**
   * Handle onboarding skip
   */
  const handleOnboardingSkip = () => {
    setRunOnboarding(false);
    setHasCompletedOnboarding(true);
    if (onOnboardingSkip) {
      onOnboardingSkip();
    }
  };

  /**
   * Handle textarea content change
   */
  const handleTextChange = e => {
    if (pendingText !== null) {
      setPendingText(e.target.value);
    } else {
      setText(e.target.value);
    }
  };

  /**
   * Handle textarea blur event
   * Only save if the click wasn't in the menu
   */
  const handleBlur = e => {
    // Verify if the click was inside the options menu
    if (menuRef.current && menuRef.current.contains(e.relatedTarget)) {
      // If the click was inside the menu, do not save
      return;
    }

    // Only save if there was a change
    if (onChange && text !== value) {
      onChange(text);
    }
  };

  return (
    <div className="ai-editor-container">
      {/* Editor label */}
      {label && (
        <ControlLabel>
          <h4 className="bold editable-label">{label}</h4>
        </ControlLabel>
      )}

      {/* Text editor area */}
      <div
        className={`ai-textarea-wrapper ${
          pendingText !== null ? 'has-pending-text' : ''
        }`}
      >
        {/* Pending text indicator */}
        {pendingText !== null && (
          <div className="ai-pending-indicator">
            <AlertCircle size={16} />
            <span>Propuesta de IA pendiente</span>
          </div>
        )}

        {/* Main textarea */}
        <textarea
          ref={textareaRef}
          className={`form-control ai-textarea ${
            pendingText !== null ? 'ai-textarea-pending' : ''
          }`}
          rows={rows}
          value={pendingText !== null ? pendingText : text}
          onChange={handleTextChange}
          onBlur={handleBlur}
          onMouseUp={handleSelection}
          onKeyUp={handleSelection}
          placeholder={placeholder}
        />
      </div>

      {/* AI options menu */}
      {menuVisible && (
        <TextMenu
          isLoading={isLoading}
          onAskAI={handleAskAI}
          onOptionSelect={handleTransformText}
          selectedText={selection.text}
          menuRef={menuRef}
          referenceElement={textareaRef.current}
        />
      )}

      {/* Accept/Reject buttons for AI suggestions */}
      {pendingText !== null && (
        <ActionButtons
          onAccept={acceptChange}
          onReject={rejectChange}
          isLoading={isLoading}
        />
      )}

      {/* Query response panel */}
      {infoResponse && (
        <QueryAssistant
          infoResponse={infoResponse}
          onClose={closeInfoResponse}
        />
      )}

      {/* Onboarding tutorial */}
      <AITextEditorOnboarding
        runOnboarding={runOnboarding}
        hasCompletedOnboarding={hasCompletedOnboarding}
        onOnboardingComplete={handleOnboardingComplete}
        onOnboardingSkip={handleOnboardingSkip}
        onDemoTextInsert={handleDemoTextInsert}
      />
    </div>
  );
}
